/**
 * Konkord: the size and frame rate a stream can really send on the streamer's
 * upload. Every viewer's picture goes up the same connection, so when it is too
 * small the picture freezes and the streamer's game lags. A smaller stream fixes
 * that; a moment of bad internet shouldn't cause it.
 *
 * Every check (2 s) says whether the links carry what the current level needs:
 * - a spike: nothing changes (the bitrate alone follows it, see #updateShared).
 *   The browser's estimate of a link takes a good while to climb back after one,
 *   so while it is climbing the check doesn't count as tight: only an estimate
 *   that stays down, or climbs and loses packets, says it's really short.
 * - internet that is really short: most of the last 20 s tight (from 18 s on), or more than half
 *   of the last minute (a connection that keeps failing, with moments of calm).
 *   Goes straight to the level that fits.
 * - back up one level at a time after a calm minute. A climb that doesn't hold
 *   comes back down in seconds and waits twice as long before the next try (up
 *   to 10 minutes), since the estimate of a link only proves itself by using it.
 */

export type FitLevel = { height: number; fps: number };

/** The bitrate a level needs, in bits per second */
type Need = (level: FitLevel) => number;

/** Links carrying less than this share of what the level needs are tight */
const TIGHT = 0.7;
/** A level that fits leaves this margin on top of what it needs */
const FITS = 0.85;
/** An estimate growing this much from one check to the next is still climbing */
const CLIMBING = 1.06;

const CHECK_MS = 2000;
/** Most of the last 20 s: 9 tight checks are 18 s */
const SHORT_WINDOW = 10;
const SHORT_TIGHT = 9;
/** Over half of the last minute */
const LONG_WINDOW = 30;
const LONG_TIGHT = 16;

const CLIMB_WAIT_MS = 60_000;
const CLIMB_WAIT_MAX_MS = 10 * 60_000;
/** A climb is proved or not within this long; proved for longer, the wait resets */
const CLIMB_TRIAL_MS = 30_000;
const CLIMB_PROVED_MS = 5 * 60_000;
/** During a trial this many tight checks in a row bring it back */
const TRIAL_TIGHT = 3;

const LOWER_HEIGHTS = [1080, 720, 540];

/**
 * From what the streamer picked down to the least: 1440p 60 → 1080p 60 → 1080p 30
 * → 720p 30 → 540p 30 (motion first above Full HD, where the size is hardly seen)
 */
export function fitLevels(top: FitLevel): FitLevel[] {
  const levels = [top];
  let { height, fps } = top;
  for (;;) {
    if (height > 1080) height = 1080;
    else if (fps > 30) fps = 30;
    else {
      const lower: number | undefined = LOWER_HEIGHTS.find((h) => h < height);
      if (lower === undefined) return levels;
      height = lower;
    }
    levels.push({ height, fps });
  }
}

export class StreamFit {
  #top?: FitLevel;
  #levels: FitLevel[] = [];
  #index = 0;
  #recent: boolean[] = [];
  #calmSince = 0;
  #climbedAt = -Infinity;
  #wait = CLIMB_WAIT_MS;
  #lastCarry?: number;

  /** The level to send now (the top one when nothing is wrong) */
  get level(): FitLevel | undefined {
    return this.#levels[this.#index];
  }

  /** Whether there's nowhere lower to go */
  get atFloor() {
    return this.#index === this.#levels.length - 1;
  }

  /** Whether the stream is below what the streamer picked */
  get lowered() {
    return this.#index > 0;
  }

  /**
   * @param top what the streamer picked
   * @param carry what the links carry, in bits per second (undefined: not known,
   * like a link that just connected, whose estimate starts low)
   */
  check(
    top: FitLevel,
    carry: number | undefined,
    need: Need,
    now = Date.now(),
    /** a link lost packets just now */
    lossy = false,
  ) {
    if (top.height !== this.#top?.height || top.fps !== this.#top?.fps) {
      // the streamer picked another quality: a new start
      this.#top = top;
      this.#levels = fitLevels(top);
      this.#index = 0;
      this.#recent = [];
      this.#calmSince = now;
      this.#climbedAt = -Infinity;
      this.#wait = CLIMB_WAIT_MS;
    }
    if (carry === undefined) return this.level!;

    const current = this.level!;
    const climbing =
      this.#lastCarry !== undefined && carry > this.#lastCarry * CLIMBING;
    this.#lastCarry = carry;
    const tight = carry < need(current) * TIGHT && (!climbing || lossy);
    this.#recent.push(tight);
    if (this.#recent.length > LONG_WINDOW) this.#recent.shift();
    if (tight) this.#calmSince = now;

    const inTrial = now - this.#climbedAt < CLIMB_TRIAL_MS;
    if (this.#isReallyShort(inTrial)) {
      this.#goDown(carry, need);
      if (inTrial) this.#wait = Math.min(this.#wait * 2, CLIMB_WAIT_MAX_MS);
      this.#climbedAt = -Infinity;
      this.#recent = [];
      this.#calmSince = now;
    } else if (this.#index > 0 && now - this.#calmSince >= this.#wait) {
      this.#index--;
      this.#climbedAt = now;
      this.#calmSince = now;
      this.#recent = [];
    } else if (now - this.#climbedAt >= CLIMB_PROVED_MS && this.#index === 0) {
      this.#wait = CLIMB_WAIT_MS;
    }
    return this.level!;
  }

  #isReallyShort(inTrial: boolean) {
    const recent = this.#recent;
    if (inTrial) {
      return (
        recent.length >= TRIAL_TIGHT &&
        recent.slice(-TRIAL_TIGHT).every(Boolean)
      );
    }
    const count = (list: boolean[]) => list.filter(Boolean).length;
    return (
      (recent.length >= SHORT_WINDOW &&
        count(recent.slice(-SHORT_WINDOW)) >= SHORT_TIGHT) ||
      count(recent) >= LONG_TIGHT
    );
  }

  #goDown(carry: number, need: Need) {
    for (let i = this.#index + 1; i < this.#levels.length; i++) {
      this.#index = i;
      if (need(this.#levels[i]) * FITS <= carry) return;
    }
  }
}

/** For tests */
export const FIT_CHECK_MS = CHECK_MS;
